/**
 * useStepUp — ask for a second sign-in method before a sensitive action (2026-09-26).
 *
 *   const { requireStepUp, withStepUp, stepUpDialog } = useStepUp();
 *   if (!(await requireStepUp({ reason: "Before turning this off, confirm it's you." }))) return;
 *   const res = await withStepUp(() => disableTwoFactor());   // retries ONCE after STEP_UP_REQUIRED
 *   …render {stepUpDialog} once in the component.
 *
 * `requireStepUp()` resolves true straight away when the session is already a FRESH aal2 (second
 * factor proven within `maxAgeMinutes`, default 10 — the backend's rule for enable/disable/new codes),
 * otherwise it opens the "Confirm it's you" dialog and resolves true when the person proves a second
 * method (the new token is already stored), false if they close it. The server re-checks every
 * protected call, so this is a courtesy that saves a failed request — never the security itself.
 */
import React from "react";
import { StepUpDialog } from "../components/StepUpDialog";
import { STEP_UP_FRESH_MINUTES, isFreshAal2, isStepUpRequiredError, readStoredAssurance } from "../utils/assurance";

export interface RequireStepUpOptions {
    /** Plain words shown under "Confirm it's you". */
    reason?: string;
    /** Ask even when the session looks fresh (e.g. the server just answered STEP_UP_REQUIRED). */
    force?: boolean;
}

/**
 * Run `run`; if the server answers STEP_UP_REQUIRED, ask for a step-up and run it ONE more time.
 * A refused or cancelled step-up rethrows the original error. Pure (no React) so it is unit-tested.
 */
export async function runWithStepUpRetry<T>(
    run: () => Promise<T>,
    requireStepUp: (opts: RequireStepUpOptions) => Promise<boolean>,
    opts: RequireStepUpOptions = {},
): Promise<T> {
    try {
        return await run();
    } catch (err) {
        if (!isStepUpRequiredError(err)) throw err;
        const ok = await requireStepUp({ ...opts, force: true });
        if (!ok) throw err;
        return run();
    }
}

export function useStepUp({ maxAgeMinutes = STEP_UP_FRESH_MINUTES }: { maxAgeMinutes?: number } = {}) {
    const [open, setOpen] = React.useState<{ reason?: string } | null>(null);
    const resolver = React.useRef<((ok: boolean) => void) | null>(null);

    const finish = React.useCallback((ok: boolean) => {
        const resolve = resolver.current;
        resolver.current = null;
        setOpen(null);
        resolve?.(ok);
    }, []);

    // Settle a pending promise if the component goes away with the dialog open.
    React.useEffect(() => () => { resolver.current?.(false); resolver.current = null; }, []);

    const requireStepUp = React.useCallback(
        (opts: RequireStepUpOptions = {}): Promise<boolean> => {
            // Read the stored token, not React state: right after an adoptToken the context may lag a render.
            if (!opts.force && isFreshAal2(readStoredAssurance(), maxAgeMinutes)) return Promise.resolve(true);
            return new Promise<boolean>((resolve) => {
                resolver.current?.(false); // a second request supersedes the first
                resolver.current = resolve;
                setOpen({ reason: opts.reason });
            });
        },
        [maxAgeMinutes],
    );

    const withStepUp = React.useCallback(
        <T,>(run: () => Promise<T>, opts: RequireStepUpOptions = {}) => runWithStepUpRetry(run, requireStepUp, opts),
        [requireStepUp],
    );

    const stepUpDialog = (
        <StepUpDialog
            isOpen={!!open}
            reason={open?.reason}
            onVerified={() => finish(true)}
            onCancel={() => finish(false)}
        />
    );

    return { requireStepUp, withStepUp, stepUpDialog, isStepUpOpen: !!open };
}

export default useStepUp;
